import { StoredLogo } from './shared/StoredLogo';

import React, { useState, useCallback, useEffect } from 'react';
import { clientsService, sitesService, sitePricingService, servicesService } from '../services';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { getApiError } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { EmailPreview } from '@/components/ui/email-preview';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { 
  Loader2, Pencil, Trash2, Building2, MapPin, 
  Phone, Mail, User, Save, Plus, Banknote, Users, X, Palette, AlertTriangle, Settings, Edit3, Link2, Eye
} from 'lucide-react';
import BudgetHistoryPanel from './BudgetHistoryPanel';

const countries = ['United Kingdom', 'United Arab Emirates'];
const auditFrequencies = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'fortnightly', label: 'Fortnightly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'bi-monthly', label: 'Bi-Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'semi-annual', label: 'Semi-Annual' },
  { value: 'annual', label: 'Annual' },
  { value: 'adhoc', label: 'Ad-hoc' },
  { value: 'custom', label: 'Custom' }
];

// Preset colours for quick selection - matches CustomersPage palette
const presetColours = [
  { name: 'Sky Blue', hex: '#0EA5E9' },
  { name: 'Blue', hex: '#3B82F6' },
  { name: 'Indigo', hex: '#6366F1' },
  { name: 'Navy', hex: '#1E3A8A' },
  { name: 'Emerald', hex: '#10B981' },
  { name: 'Green', hex: '#22C55E' },
  { name: 'Teal', hex: '#14B8A6' },
  { name: 'Forest', hex: '#166534' },
  { name: 'Amber', hex: '#F59E0B' },
  { name: 'Orange', hex: '#F97316' },
  { name: 'Red', hex: '#EF4444' },
  { name: 'Rose', hex: '#E11D48' },
  { name: 'Purple', hex: '#8B5CF6' },
  { name: 'Violet', hex: '#7C3AED' },
  { name: 'Pink', hex: '#EC4899' },
  { name: 'Fuchsia', hex: '#D946EF' },
  { name: 'Slate', hex: '#64748B' },
  { name: 'Grey', hex: '#6B7280' },
  { name: 'Zinc', hex: '#71717A' },
  { name: 'Stone', hex: '#78716C' },
  { name: 'Cyan', hex: '#06B6D4' },
  { name: 'Lime', hex: '#84CC16' },
  { name: 'Yellow', hex: '#EAB308' },
  { name: 'Coral', hex: '#FF6B6B' },
  { name: 'Plum', hex: '#9333EA' },
];

/**
 * IndependentCustomerDialog - Simplified single-window editor for Independent customers
 * 
 * For independent customers, the customer and site are effectively the same entity.
 * This component provides a streamlined interface where:
 * - Address and contacts are entered once (synced to both customer and site)
 * - Pricing is displayed inline (no separate window)
 * - All data is saved atomically
 */
export default function IndependentCustomerDialog({
  open,
  onOpenChange,
  customer,
  onSaved,
  onDeleted,
  usedColours = []  // Colours already used by other customers
}) {
  const { formatCurrency, currencySymbol, region, settings } = useSettings();
  const { isAuditor, isAdmin, canManageSites, canManageLogos, isScheduler } = useAuth();
  const phonePlaceholder = region === 'UAE' ? '+971 50 123 4567' : '+44 7700 900000';
  
  // Auditor-only: can view customer data but only edit specific fields
  // (contacts, budget cost of sales %, stockholding period)
  const isAuditorOnly = isAuditor && !isScheduler && !isAdmin;
  
  // Auditor can only edit contact flags, budget, stockholding - NOT pricing
  const canEditPricing = isAdmin || canManageSites;
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Form state - unified customer/site data
  const [form, setForm] = useState({
    name: '',
    colour: null,
    address_line_1: '',
    address_line_2: '',
    city: '',
    postcode: '',
    country: region === 'UAE' ? 'United Arab Emirates' : 'United Kingdom',
    audit_frequency: '',
    custom_frequency_days: '',
    year_end_month: '',
    year_end_day: '',
    is_active: true,
    // Phase 1A fields (auditor-editable) — Beverages
    budget_cost_of_sales_pct: '',
    days_stockholding_period: '',
    // Food fields
    food_budget_cost_of_sales_pct: '',
    food_days_stockholding_period: '',
    custom_schedule_email_script: ''
  });
  
  // Related data
  const [site, setSite] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [services, setServices] = useState([]);
  
  // Contact edit state
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({ 
    name: '', 
    email: '', 
    phone: '', 
    role: '', 
    is_primary: false,
    for_job_scheduling: false,
    for_job_reports: false
  });
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  
  // Pricing edit state
  const [editingPricing, setEditingPricing] = useState(null);
  const [pricingForm, setPricingForm] = useState({ service_id: '', price: '', description: '', is_default: false });
  const [pricingDialogOpen, setPricingDialogOpen] = useState(false);
  
  // Email script dialog state
  const [emailScriptDialogOpen, setEmailScriptDialogOpen] = useState(false);
  const [tempEmailScript, setTempEmailScript] = useState('');
  const [showEmailPreview, setShowEmailPreview] = useState(false);
  
  // Color confirmation dialog state
  const [colorConfirmDialogOpen, setColorConfirmDialogOpen] = useState(false);
  const [pendingColour, setPendingColour] = useState(null);
  const [colourInUseBy, setColourInUseBy] = useState(null);

  // Convert to Group dialog state
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [converting, setConverting] = useState(false);

  // Attach to Group dialog state
  const [attachDialogOpen, setAttachDialogOpen] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [groupClients, setGroupClients] = useState([]);

  // Stocktake Settings state
  const [dialogTab, setDialogTab] = useState('profile');

  // Handle colour selection with duplicate check
  const handleColourSelect = async (colourHex) => {
    if (!colourHex) {
      setForm(prev => ({ ...prev, colour: null }));
      return;
    }
    
    // Check if colour is already used
    try {
      const result = await clientsService.checkColour(colourHex, customer?.id);
      if (result.in_use) {
        // Show confirmation dialog
        setPendingColour(colourHex);
        setColourInUseBy(result.client_name);
        setColorConfirmDialogOpen(true);
      } else {
        // Colour is not in use, apply directly
        setForm(prev => ({ ...prev, colour: colourHex }));
      }
    } catch (err) {
      console.error('Failed to check colour:', err);
      // If check fails, still allow selection
      setForm(prev => ({ ...prev, colour: colourHex }));
    }
  };

  const confirmColourSelection = () => {
    if (pendingColour) {
      setForm(prev => ({ ...prev, colour: pendingColour }));
    }
    setColorConfirmDialogOpen(false);
    setPendingColour(null);
    setColourInUseBy(null);
  };

  // Load customer data when dialog opens
  const loadData = useCallback(async () => {
    if (!customer || !open) return;
    
    setLoading(true);
    try {
      // Set form with customer data
      setForm({
        name: customer.name || '',
        colour: customer.colour || null,
        address_line_1: customer.address_line_1 || '',
        address_line_2: customer.address_line_2 || '',
        city: customer.city || '',
        postcode: customer.postcode || '',
        country: customer.country || (region === 'UAE' ? 'United Arab Emirates' : 'United Kingdom'),
        audit_frequency: '',
        is_active: customer.is_active !== false,
        // D3 — AP applicability (independent clients supported; no exclusions since single-site)
        ap_applicability_enabled: customer.ap_applicability_enabled === true,
      });
      
      // Load services for pricing dropdown
      const servicesData = await servicesService.list();
      setServices(servicesData);
      
      // Find the independent site for this customer first
      const allSites = await sitesService.list({ region, limit: 500 });
      const customerSite = allSites.find(s => s.independent_client_id === customer.id);
      
      if (customerSite) {
        setSite(customerSite);
        // Update form with site-specific data (Phase 1A fields)
        setForm(prev => ({
          ...prev,
          audit_frequency: customerSite.audit_frequency || '',
          custom_frequency_days: customerSite.custom_frequency_days || '',
          year_end_month: customerSite.year_end_month?.toString() || '',
          year_end_day: customerSite.year_end_day?.toString() || '',
          budget_cost_of_sales_pct: customerSite.budget_cost_of_sales_pct || '',
          days_stockholding_period: customerSite.days_stockholding_period || '',
          food_budget_cost_of_sales_pct: customerSite.food_budget_cost_of_sales_pct || '',
          food_days_stockholding_period: customerSite.food_days_stockholding_period || '',
          custom_schedule_email_script: customerSite.custom_schedule_email_script || ''
        }));
        
        // Load site contacts (with for_job_scheduling and for_job_reports flags)
        try {
          const siteContacts = await sitesService.listContacts(customerSite.id);
          setContacts(siteContacts);
        } catch (err) {
          console.error('Failed to load site contacts:', err);
          setContacts([]);
        }
        
        // Load site pricing
        try {
          const sitePricing = await sitePricingService.getForSite(customerSite.id);
          setPricing(sitePricing);
        } catch (err) {
          setPricing([]);
        }

      } else {
        setSite(null);
        setContacts([]);
        setPricing([]);
      }
    } catch (err) {
      console.error('Failed to load customer data:', err);
      toast.error('Failed to load customer data');
    } finally {
      setLoading(false);
    }
  }, [customer, open, region]);

  useEffect(() => {
    if (open && customer) {
      setDialogTab('profile');
      loadData();
      // Load group clients for attach functionality
      loadGroupClients();
    }
  }, [open, customer, loadData]);

  // Load group clients for "Attach to Group" functionality
  const loadGroupClients = async () => {
    try {
      const clients = await clientsService.list({ region });
      const groups = clients.filter(c => c.client_kind === 'group' && !c.deleted_at);
      setGroupClients(groups);
    } catch (err) {
      console.error('Failed to load group clients:', err);
      setGroupClients([]);
    }
  };

  // Save handler - updates both customer and site
  const handleSave = async () => {
    if (!form.name?.trim()) {
      toast.error('Customer name is required');
      return;
    }

    setSaving(true);
    try {
      // Update customer
      await clientsService.update(customer.id, {
        name: form.name,
        colour: form.colour,
        address_line_1: form.address_line_1,
        address_line_2: form.address_line_2,
        city: form.city,
        postcode: form.postcode,
        country: form.country,
        is_active: form.is_active,
        // D3 — AP applicability (Scheduler/Admin only; backend enforces authorisation)
        ...((isAdmin || isScheduler) ? { ap_applicability_enabled: form.ap_applicability_enabled } : {}),
      });
      
      // Update site with same address and pricing info
      if (site) {
        if (isAuditorOnly) {
          // Auditors use the restricted endpoint for allowed fields only
          await sitesService.updateAuditorFields(site.id, {
            budget_cost_of_sales_pct: form.budget_cost_of_sales_pct ? parseFloat(form.budget_cost_of_sales_pct) : null,
            days_stockholding_period: form.days_stockholding_period ? parseInt(form.days_stockholding_period) : null,
            food_budget_cost_of_sales_pct: form.food_budget_cost_of_sales_pct ? parseFloat(form.food_budget_cost_of_sales_pct) : null,
            food_days_stockholding_period: form.food_days_stockholding_period ? parseInt(form.food_days_stockholding_period) : null,
            custom_schedule_email_script: form.custom_schedule_email_script || null
          });
        } else {
          await sitesService.update(site.id, {
            name: form.name,
            address_line_1: form.address_line_1,
            address_line_2: form.address_line_2,
            city: form.city,
            postcode: form.postcode,
            country: form.country,
            audit_frequency: form.audit_frequency || null,
            custom_frequency_days: form.audit_frequency === 'custom' && form.custom_frequency_days ? parseInt(form.custom_frequency_days) : null,
            year_end_month: form.year_end_month ? parseInt(form.year_end_month) : null,
            year_end_day: form.year_end_day ? parseInt(form.year_end_day) : null,
            is_active: form.is_active,
            budget_cost_of_sales_pct: form.budget_cost_of_sales_pct ? parseFloat(form.budget_cost_of_sales_pct) : null,
            days_stockholding_period: form.days_stockholding_period ? parseInt(form.days_stockholding_period) : null,
            food_budget_cost_of_sales_pct: form.food_budget_cost_of_sales_pct ? parseFloat(form.food_budget_cost_of_sales_pct) : null,
            food_days_stockholding_period: form.food_days_stockholding_period ? parseInt(form.food_days_stockholding_period) : null,
            custom_schedule_email_script: form.custom_schedule_email_script || null
          });
        }
      }

      toast.success('Customer saved successfully');
      onSaved?.();
    } catch (err) {
      console.error('Failed to save:', err);
      toast.error(getApiError(err, 'Failed to save customer'));
    } finally {
      setSaving(false);
    }
  };

  // Contact handlers
  const openContactDialog = (contact = null) => {
    setEditingContact(contact);
    setContactForm(contact ? {
      name: contact.name || '',
      email: contact.email || '',
      phone: contact.phone || '',
      role: contact.role || '',
      is_primary: contact.is_primary || false,
      for_job_scheduling: contact.for_job_scheduling || false,
      for_job_reports: contact.for_job_reports || false
    } : { 
      name: '', 
      email: '', 
      phone: '', 
      role: '', 
      is_primary: false,
      for_job_scheduling: false,
      for_job_reports: false
    });
    setContactDialogOpen(true);
  };

  const handleSaveContact = async () => {
    if (!contactForm.name?.trim()) {
      toast.error('Contact name is required');
      return;
    }
    
    if (!site) {
      toast.error('Site not loaded - cannot save contact');
      return;
    }

    try {
      if (editingContact) {
        await sitesService.updateContact(site.id, editingContact.id, contactForm);
        toast.success('Contact updated');
      } else {
        await sitesService.createContact(site.id, contactForm);
        toast.success('Contact added');
      }
      setContactDialogOpen(false);
      
      // Reload site contacts
      const siteContacts = await sitesService.listContacts(site.id);
      setContacts(siteContacts);
    } catch (err) {
      toast.error(getApiError(err, 'Failed to save contact'));
    }
  };

  const handleDeleteContact = async (contact) => {
    if (!site) {
      toast.error('Site not loaded - cannot delete contact');
      return;
    }
    
    try {
      await sitesService.deleteContact(site.id, contact.id);
      toast.success('Contact deleted');
      const siteContacts = await sitesService.listContacts(site.id);
      setContacts(siteContacts);
    } catch (err) {
      toast.error('Failed to delete contact');
    }
  };

  // Pricing handlers
  const openPricingDialog = (pricingItem = null) => {
    setEditingPricing(pricingItem);
    setPricingForm(pricingItem ? {
      service_id: pricingItem.service_id?.toString() || '',
      price: pricingItem.price?.toString() || '',
      description: pricingItem.description || '',
      is_default: pricingItem.is_default || false
    } : { service_id: '', price: '', description: '', is_default: false });
    setPricingDialogOpen(true);
  };

  const handleSavePricing = async () => {
    if (!pricingForm.service_id || !pricingForm.price) {
      toast.error('Service and price are required');
      return;
    }

    if (!site) {
      toast.error('No site found for pricing');
      return;
    }

    try {
      const pricingData = {
        site_id: site.id,
        service_id: parseInt(pricingForm.service_id),
        price: parseFloat(pricingForm.price),
        description: pricingForm.description || null,
        is_default: pricingForm.is_default || false
      };

      if (editingPricing) {
        await sitePricingService.update(editingPricing.id, pricingData);
        toast.success('Pricing updated');
      } else {
        await sitePricingService.create(pricingData);
        toast.success('Pricing added');
      }
      setPricingDialogOpen(false);
      
      // Reload pricing
      const sitePricing = await sitePricingService.getForSite(site.id);
      setPricing(sitePricing);
    } catch (err) {
      console.error('Pricing save error:', err);
      toast.error(getApiError(err, 'Failed to save pricing'));
    }
  };

  const handleDeletePricing = async (pricingItem) => {
    try {
      await sitePricingService.delete(pricingItem.id);
      toast.success('Pricing deleted');
      const sitePricing = await sitePricingService.getForSite(site.id);
      setPricing(sitePricing);
    } catch (err) {
      toast.error('Failed to delete pricing');
    }
  };

  // Convert independent to group
  const handleConvertToGroup = async () => {
    if (!customer) return;
    
    setConverting(true);
    try {
      await clientsService.convertToGroup(customer.id);
      toast.success('Customer converted to group successfully');
      setConvertDialogOpen(false);
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to convert to group'));
    } finally {
      setConverting(false);
    }
  };

  // Attach independent to existing group
  const handleAttachToGroup = async () => {
    if (!customer || !selectedGroupId) return;
    
    setAttaching(true);
    try {
      await clientsService.attachToGroup(customer.id, parseInt(selectedGroupId));
      toast.success('Customer attached to group');
      setAttachDialogOpen(false);
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to attach to group'));
    } finally {
      setAttaching(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh]" data-testid="independent-customer-dialog">
          <DialogHeader>
            <div className="flex items-center gap-3">
              {form.colour ? (
                <div className="w-5 h-5 rounded-full flex-shrink-0" style={{ backgroundColor: form.colour }} />
              ) : (
                <MapPin className="h-5 w-5 text-primary" />
              )}
              <div>
                <DialogTitle data-testid="independent-customer-dialog-title">
                  {customer?.name || 'Independent Customer'}
                </DialogTitle>
                <DialogDescription>
                  <Badge variant="secondary" className="mt-1">Independent</Badge>
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          <Tabs value={dialogTab} onValueChange={setDialogTab} className="w-full">
            <TabsContent value="profile" className="mt-0">
          <div className="overflow-y-auto pr-2" style={{ maxHeight: 'calc(90vh - 260px)' }}>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Customer Details */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                    <Building2 className="h-4 w-4" /> Customer Details
                    {isAuditorOnly && <Badge variant="outline" className="text-xs">View Only</Badge>}
                  </h4>
                  <div className="flex gap-4">
                    <div className="space-y-2 flex-1">
                      <Label>Customer Name *</Label>
                      <Input
                        value={form.name}
                        onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Customer name"
                        disabled={isAuditorOnly}
                        className={isAuditorOnly ? 'bg-muted cursor-not-allowed' : ''}
                        data-testid="independent-customer-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <div className="flex items-center gap-3 h-10">
                        <Switch
                          checked={form.is_active}
                          onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_active: checked }))}
                          disabled={isAuditorOnly}
                          data-testid="independent-active-toggle"
                        />
                        <span className={`text-sm ${form.is_active ? 'text-green-600' : 'text-muted-foreground'}`}>
                          {form.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    {/* D3 — AP Applicability toggle (Scheduler/Admin only; single-site so no exclusions panel) */}
                    {(isAdmin || isScheduler) && (
                      <div className="space-y-2">
                        <Label>AP Applicability</Label>
                        <div className="flex items-center gap-3 h-10">
                          <Switch
                            checked={form.ap_applicability_enabled === true}
                            onCheckedChange={(checked) => setForm(prev => ({ ...prev, ap_applicability_enabled: checked }))}
                            data-testid="independent-ap-enabled-toggle"
                          />
                          <span className="text-sm text-muted-foreground">
                            {form.ap_applicability_enabled ? 'AP eligible (single site)' : 'AP not enabled'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Colour Picker */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Palette className="h-4 w-4" /> Calendar Colour
                    </Label>
                    <div className={`flex flex-wrap gap-2 ${isAuditorOnly ? 'opacity-60 pointer-events-none' : ''}`}>
                      {presetColours.map(colour => {
                        // Check if colour is used by another customer (not this one)
                        const isUsed = usedColours.includes(colour.hex) && form.colour !== colour.hex;
                        return (
                          <button
                            key={colour.hex}
                            type="button"
                            onClick={() => handleColourSelect(colour.hex)}
                            disabled={isAuditorOnly}
                            className={`w-7 h-7 rounded-full border-2 transition-all relative ${
                              form.colour === colour.hex ? 'border-foreground scale-110' : 'border-transparent hover:scale-105'
                            }`}
                            style={{ backgroundColor: colour.hex }}
                            title={`${colour.name}${isUsed ? ' (used by another customer)' : ''}`}
                            data-testid={`colour-${colour.name.toLowerCase().replace(' ', '-')}`}
                          >
                            {isUsed && (
                              <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-bold">•</span>
                            )}
                          </button>
                        );
                      })}
                      {form.colour && !isAuditorOnly && (
                        <button
                          type="button"
                          onClick={() => handleColourSelect(null)}
                          className="w-7 h-7 rounded-full border-2 border-dashed border-muted-foreground flex items-center justify-center hover:bg-muted"
                          title="Remove colour"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  {/* Site Logo (Independent customers upload at site level) */}
                  {customer && site && canManageLogos && (
                    <div className="space-y-2 pt-2">
                      <Label className="flex items-center gap-2">
                        <Eye className="h-4 w-4" /> Site Logo
                      </Label>
                      <div className="flex items-center gap-4">
                        {site?.logo_url ? (
                          <div className="relative group">
                            <StoredLogo
                              logoKey={site.logo_url}
                              alt="Site logo"
                              className="h-16 w-auto max-w-[160px] object-contain border rounded p-1 bg-white"
                              data-testid="independent-site-logo-preview"
                            />
                            <Button 
                              size="icon" variant="destructive"
                              className="absolute -top-2 -right-2 h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={async () => {
                                if (!window.confirm('Remove this logo?')) return;
                                try {
                                  const { default: api } = await import('../services/api');
                                  await api.delete(`/sites/${site.id}/logo`);
                                  setSite(prev => ({ ...prev, logo_url: null }));
                                  toast.success('Logo removed');
                                } catch (err) { toast.error('Failed to remove logo'); }
                              }}
                              data-testid="remove-independent-site-logo"
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <label className="cursor-pointer">
                              <input 
                                type="file" 
                                accept=".png,.jpg,.jpeg,.svg,.webp"
                                className="hidden"
                                data-testid="upload-independent-site-logo-input"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  if (file.size > 5 * 1024 * 1024) { toast.error('File too large (max 5MB)'); return; }
                                  const formData = new FormData();
                                  formData.append('file', file);
                                  try {
                                    const { default: api } = await import('../services/api');
                                    const res = await api.post(`/sites/${site.id}/logo`, formData);
                                    setSite(prev => ({ ...prev, logo_url: res.data.logo_url }));
                                    toast.success('Logo uploaded successfully');
                                  } catch (err) { toast.error(getApiError(err, 'Upload failed')); }
                                  e.target.value = '';
                                }}
                              />
                              <Button variant="outline" size="sm" asChild>
                                <span data-testid="upload-independent-site-logo-btn"><Save className="h-3 w-3 mr-1" /> Upload Logo</span>
                              </Button>
                            </label>
                            <span className="text-xs text-muted-foreground">PNG, JPG, SVG (max 5MB)</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <Separator />
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                    <MapPin className="h-4 w-4" /> Address
                    {isAuditorOnly && <Badge variant="outline" className="text-xs">View Only</Badge>}
                  </h4>
                  <div className="grid grid-cols-1 gap-3">
                    <div className="space-y-2">
                      <Label>Address Line 1</Label>
                      <Input
                        value={form.address_line_1}
                        onChange={(e) => setForm(prev => ({ ...prev, address_line_1: e.target.value }))}
                        placeholder="Street address"
                        disabled={isAuditorOnly}
                        className={isAuditorOnly ? 'bg-muted cursor-not-allowed' : ''}
                        data-testid="independent-address-1"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Address Line 2</Label>
                      <Input
                        value={form.address_line_2}
                        onChange={(e) => setForm(prev => ({ ...prev, address_line_2: e.target.value }))}
                        placeholder="Suite, building, etc."
                        disabled={isAuditorOnly}
                        className={isAuditorOnly ? 'bg-muted cursor-not-allowed' : ''}
                        data-testid="independent-address-2"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-2">
                        <Label>City</Label>
                        <Input
                          value={form.city}
                          onChange={(e) => setForm(prev => ({ ...prev, city: e.target.value }))}
                          placeholder="City"
                          disabled={isAuditorOnly}
                          className={isAuditorOnly ? 'bg-muted cursor-not-allowed' : ''}
                          data-testid="independent-city"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Postcode</Label>
                        <Input
                          value={form.postcode}
                          onChange={(e) => setForm(prev => ({ ...prev, postcode: e.target.value }))}
                          placeholder="Postcode"
                          disabled={isAuditorOnly}
                          className={isAuditorOnly ? 'bg-muted cursor-not-allowed' : ''}
                          data-testid="independent-postcode"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Country</Label>
                        <Select 
                          value={form.country} 
                          onValueChange={(v) => setForm(prev => ({ ...prev, country: v }))}
                          disabled={isAuditorOnly}
                        >
                          <SelectTrigger data-testid="independent-country" className={isAuditorOnly ? 'bg-muted cursor-not-allowed' : ''}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Contacts */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                      <Users className="h-4 w-4" /> Contacts ({contacts.length})
                    </h4>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => openContactDialog()}
                      data-testid="independent-add-contact-btn"
                    >
                      <Plus className="h-3 w-3 mr-1" /> Add
                    </Button>
                  </div>
                  {contacts.length === 0 ? (
                    <p className="text-sm text-muted-foreground italic">No contacts added yet</p>
                  ) : (
                    <div className="space-y-2">
                      {contacts.map(contact => (
                        <div 
                          key={contact.id} 
                          className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                          data-testid={`independent-contact-${contact.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <User className="h-4 w-4 text-muted-foreground" />
                            <div>
                              <div className="font-medium text-sm flex items-center gap-2">
                                {contact.name}
                                {contact.for_job_scheduling && (
                                  <Badge variant="outline" className="text-xs text-blue-600 border-blue-300">Sched. Pub. &amp; Billing</Badge>
                                )}
                                {contact.for_job_reports && (
                                  <Badge variant="outline" className="text-xs text-green-600 border-green-300">Report Recipient</Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground flex items-center gap-3 mt-0.5">
                                {contact.email && (
                                  <span className="flex items-center gap-1">
                                    <Mail className="h-3 w-3" /> {contact.email}
                                  </span>
                                )}
                                {contact.phone && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="h-3 w-3" /> {contact.phone}
                                  </span>
                                )}
                                {contact.role && (
                                  <span className="text-muted-foreground/70">({contact.role})</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button 
                              type="button"
                              variant="ghost" 
                              size="icon" 
                              className="h-7 w-7" 
                              onClick={(e) => {
                                e.stopPropagation();
                                openContactDialog(contact);
                              }}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button 
                              type="button"
                              variant="ghost" 
                              size="icon" 
                              className="h-7 w-7" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteContact(contact);
                              }}
                              data-testid={`delete-contact-${contact.id}`}
                            >
                              <Trash2 className="h-3 w-3 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Separator />

                {/* Audit Settings - Always visible (includes auditor-editable fields) */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                    <Settings className="h-4 w-4" /> Audit Settings
                  </h4>
                  
                  <div className="space-y-2">
                    <Label>Audit Frequency</Label>
                    <Select 
                      value={form.audit_frequency || '__none__'} 
                      onValueChange={(v) => setForm(prev => ({ ...prev, audit_frequency: v === '__none__' ? '' : v, custom_frequency_days: v !== 'custom' ? '' : prev.custom_frequency_days }))}
                      disabled={isAuditorOnly}
                    >
                      <SelectTrigger data-testid="independent-audit-frequency" className={`w-[200px] ${isAuditorOnly ? 'bg-muted cursor-not-allowed' : ''}`}>
                        <SelectValue placeholder="Select frequency" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Not set</SelectItem>
                        {auditFrequencies.map(f => (
                          <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {form.audit_frequency === 'custom' && (
                      <div className="mt-2">
                        <Label className="text-xs text-muted-foreground">Days between audits</Label>
                        <Input
                          type="number"
                          min="1"
                          max="730"
                          placeholder="e.g. 45"
                          value={form.custom_frequency_days || ''}
                          onChange={(e) => setForm(prev => ({ ...prev, custom_frequency_days: e.target.value }))}
                          disabled={isAuditorOnly}
                          data-testid="independent-custom-frequency-days"
                        />
                      </div>
                    )}
                  </div>
                  
                  {/* Year End */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Year End</Label>
                    <div className="flex gap-2">
                      <Select
                        value={form.year_end_month || ''}
                        onValueChange={(v) => setForm(prev => ({ ...prev, year_end_month: v, year_end_day: '' }))}
                        disabled={isAuditorOnly}
                        data-testid="independent-year-end-month"
                      >
                        <SelectTrigger className="w-36">
                          <SelectValue placeholder="Month..." />
                        </SelectTrigger>
                        <SelectContent>
                          {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m, i) => (
                            <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={form.year_end_day || ''}
                        onValueChange={(v) => setForm(prev => ({ ...prev, year_end_day: v }))}
                        disabled={isAuditorOnly || !form.year_end_month}
                        data-testid="independent-year-end-day"
                      >
                        <SelectTrigger className="w-24">
                          <SelectValue placeholder="Day..." />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                            <SelectItem key={d} value={String(d)}>{d}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {(form.year_end_month || form.year_end_day) && !isAuditorOnly && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-9 px-2 text-xs text-muted-foreground hover:text-destructive hover:border-destructive"
                          onClick={() => setForm(prev => ({ ...prev, year_end_month: '', year_end_day: '' }))}
                          data-testid="independent-year-end-clear"
                          title="Clear year-end date"
                        >
                          <X className="h-3.5 w-3.5 mr-1" />Clear
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">Used for YTD calculations in stocktake reports</p>
                  </div>

                  {/* Beverages Budget & Stockholding Fields */}
                  <div className="grid grid-cols-2 gap-4 pt-2">
                    <div className="space-y-2">
                      <Label>Beverages Budget COS (%)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={form.budget_cost_of_sales_pct || ''}
                        onChange={(e) => setForm(prev => ({ ...prev, budget_cost_of_sales_pct: e.target.value }))}
                        placeholder="e.g. 25.5"
                        data-testid="independent-bev-budget-cost-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Beverages Stockholding (Days)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={form.days_stockholding_period || ''}
                        onChange={(e) => setForm(prev => ({ ...prev, days_stockholding_period: e.target.value }))}
                        placeholder="e.g. 14"
                        data-testid="independent-bev-stockholding-input"
                      />
                    </div>
                  </div>
                  {/* Food Budget & Stockholding Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Food Budget COS (%)</Label>
                      <Input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        value={form.food_budget_cost_of_sales_pct || ''}
                        onChange={(e) => setForm(prev => ({ ...prev, food_budget_cost_of_sales_pct: e.target.value }))}
                        placeholder="e.g. 30.0"
                        data-testid="independent-food-budget-cost-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Food Stockholding (Days)</Label>
                      <Input
                        type="number"
                        min="0"
                        value={form.food_days_stockholding_period || ''}
                        onChange={(e) => setForm(prev => ({ ...prev, food_days_stockholding_period: e.target.value }))}
                        placeholder="e.g. 7"
                        data-testid="independent-food-stockholding-input"
                      />
                    </div>
                  </div>
                  
                  {/* Custom Email Script for Schedule Publication */}
                  <div className="space-y-2 pt-2">
                    <Label className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Schedule Publication Email Script
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Custom email template for this customer. Leave empty to use the default from Settings.
                    </p>
                    <div className="flex items-center gap-4">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          setTempEmailScript(form.custom_schedule_email_script || '');
                          setEmailScriptDialogOpen(true);
                        }}
                        data-testid="edit-customer-email-script-btn"
                      >
                        <Edit3 className="h-3 w-3 mr-1" />
                        Edit Email Script
                      </Button>
                      {form.custom_schedule_email_script ? (
                        <span className="text-xs text-muted-foreground">Custom script configured</span>
                      ) : (
                        <span className="text-xs text-amber-600">Using default from Settings</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Pricing - Hidden from auditors */}
                {canEditPricing && (
                  <>
                    <Separator />
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-semibold flex items-center gap-2 text-muted-foreground">
                          <Banknote className="h-4 w-4" /> Pricing ({pricing.length})
                        </h4>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => openPricingDialog()}
                          disabled={!site}
                          data-testid="independent-add-pricing-btn"
                        >
                          <Plus className="h-3 w-3 mr-1" /> Add
                        </Button>
                      </div>
                      
                      {/* Service Pricing List */}
                      {pricing.length === 0 ? (
                        <p className="text-sm text-muted-foreground italic">No service pricing configured</p>
                      ) : (
                        <div className="space-y-2">
                          {pricing.map(item => (
                            <div 
                              key={item.id} 
                              className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                              data-testid={`independent-pricing-${item.id}`}
                            >
                              <div>
                                <div className="font-medium text-sm flex items-center gap-2">
                                  {item.service_name}
                                  {item.is_default && (
                                    <Badge variant="secondary" className="text-[10px]">Default</Badge>
                                  )}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {item.description || 'Standard rate'}
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <span className="font-semibold text-primary">
                                  {formatCurrency(item.price)}
                                </span>
                                <div className="flex gap-1">
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-7 w-7" 
                                    onClick={() => openPricingDialog(item)}
                                  >
                                    <Pencil className="h-3 w-3" />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-7 w-7" 
                                    onClick={() => handleDeletePricing(item)}
                                  >
                                    <Trash2 className="h-3 w-3 text-destructive" />
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            {/* iter495t — Effective master price + scheduled changes +
                budget change history for INDEPENDENT sites (parity
                with group sites in CustomersPage). */}
            {site?.id && (
              <div className="space-y-2 pt-4 border-t">
                <BudgetHistoryPanel siteId={site.id} />
              </div>
            )}
          </div>
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2">
            <div className="flex flex-wrap gap-2">
              {/* Attach to Group - only for non-auditors with available groups */}
              {!isAuditorOnly && groupClients.length > 0 && (
                <Button variant="outline" size="sm" onClick={() => setAttachDialogOpen(true)}>
                  <Link2 className="h-4 w-4 mr-1" /> Attach to Group
                </Button>
              )}
              {/* Convert to Group - only for non-auditors */}
              {!isAuditorOnly && (
                <Button variant="outline" size="sm" onClick={() => setConvertDialogOpen(true)}>
                  <Building2 className="h-4 w-4 mr-1" /> Convert to Group
                </Button>
              )}
              {/* Delete - hidden for auditor-only users */}
              {!isAuditorOnly && (
                <Button 
                  variant="destructive" 
                  size="sm" 
                  onClick={() => onDeleted?.()}
                  data-testid="independent-delete-btn"
                >
                  <Trash2 className="h-4 w-4 mr-1" /> Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button 
                onClick={handleSave} 
                disabled={saving}
                data-testid="independent-save-btn"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                <Save className="h-4 w-4 mr-2" /> Save
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contact Edit Dialog */}
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingContact ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={contactForm.name}
                onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Contact name"
                data-testid="contact-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={contactForm.email}
                onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="email@example.com"
                data-testid="contact-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={contactForm.phone}
                onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))}
                placeholder={phonePlaceholder}
                data-testid="contact-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Input
                value={contactForm.role}
                onChange={(e) => setContactForm(prev => ({ ...prev, role: e.target.value }))}
                placeholder="e.g., Manager, Accounts"
                data-testid="contact-role-input"
              />
            </div>
            {/* Contact Notification Flags */}
            <div className="space-y-3 pt-2 border-t">
              <Label className="text-sm font-medium text-muted-foreground">Contact Notifications</Label>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="for_job_scheduling"
                  checked={contactForm.for_job_scheduling}
                  onChange={(e) => setContactForm(prev => ({ ...prev, for_job_scheduling: e.target.checked }))}
                  className="h-4 w-4"
                />
                <Label htmlFor="for_job_scheduling" className="cursor-pointer">Receive Schedule Publication and Billing notifications</Label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="for_job_reports"
                  checked={contactForm.for_job_reports}
                  onChange={(e) => setContactForm(prev => ({ ...prev, for_job_reports: e.target.checked }))}
                  className="h-4 w-4"
                />
                <Label htmlFor="for_job_reports" className="cursor-pointer">Receive Job Report notifications</Label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveContact} data-testid="save-contact-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pricing Edit Dialog */}
      <Dialog open={pricingDialogOpen} onOpenChange={setPricingDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingPricing ? 'Edit Pricing' : 'Add Pricing'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Service *</Label>
              <Select 
                value={pricingForm.service_id} 
                onValueChange={(v) => setPricingForm(prev => ({ ...prev, service_id: v }))}
              >
                <SelectTrigger data-testid="pricing-service-select">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
                <SelectContent>
                  {services.map(s => (
                    <SelectItem key={s.id} value={s.id.toString()}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Price ({currencySymbol}) *</Label>
              <Input
                type="number"
                step="0.01"
                value={pricingForm.price}
                onChange={(e) => setPricingForm(prev => ({ ...prev, price: e.target.value }))}
                placeholder="0.00"
                data-testid="pricing-price-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={pricingForm.description}
                onChange={(e) => setPricingForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="e.g., Standard rate, Weekend rate"
                data-testid="pricing-description-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_default"
                checked={pricingForm.is_default}
                onChange={(e) => setPricingForm(prev => ({ ...prev, is_default: e.target.checked }))}
                className="h-4 w-4"
              />
              <Label htmlFor="is_default" className="cursor-pointer">Default pricing for this service</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPricingDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSavePricing} data-testid="save-pricing-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Duplicate Colour Warning Dialog */}
      <AlertDialog open={colorConfirmDialogOpen} onOpenChange={setColorConfirmDialogOpen}>
        <AlertDialogContent data-testid="colour-duplicate-warning-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Colour Already in Use
            </AlertDialogTitle>
            <AlertDialogDescription>
              The selected colour has already been assigned to client <span className="font-semibold text-foreground">"{colourInUseBy}"</span>. 
              <br /><br />
              Are you sure you want to proceed? Having multiple customers with the same colour may make them harder to distinguish on the calendar.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setColorConfirmDialogOpen(false); setPendingColour(null); }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmColourSelection}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-colour-duplicate-btn"
            >
              Use Anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Email Script Edit Dialog */}
      <Dialog open={emailScriptDialogOpen} onOpenChange={(open) => {
        setEmailScriptDialogOpen(open);
        if (!open) setShowEmailPreview(false);
      }}>
        <DialogContent
          className="flex flex-col dialog-resizable"
          style={{ width: '56rem', maxHeight: '90vh' }}
          data-testid="customer-email-script-dialog"
        >
          <DialogHeader>
            <DialogTitle>Edit Customer Email Script</DialogTitle>
            <DialogDescription>
              Configure a custom email template for this customer. Leave empty to use the default organisation template.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-4 space-y-4">
            {!showEmailPreview ? (
              <>
                <RichTextEditor
                  value={tempEmailScript}
                  onChange={setTempEmailScript}
                  placeholder="Dear {{contact_name}},

Please find below the scheduled audit details for {{site_name}}.

{{jobs_table}}

Best regards,
{{organisation_name}}"
                  minHeight="200px"
                />
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                  <strong>Available placeholders</strong> (type these exactly as shown):
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <div className="flex items-center gap-2">
                      <code className="bg-blue-100 px-2 py-0.5 rounded text-xs font-mono">{'{{contact_name}}'}</code>
                      <span className="text-xs">Recipient's name</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="bg-blue-100 px-2 py-0.5 rounded text-xs font-mono">{'{{site_name}}'}</code>
                      <span className="text-xs">Site name</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="bg-blue-100 px-2 py-0.5 rounded text-xs font-mono">{'{{customer_name}}'}</code>
                      <span className="text-xs">Customer name</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="bg-blue-100 px-2 py-0.5 rounded text-xs font-mono">{'{{scheduled_date}}'}</code>
                      <span className="text-xs">First audit date</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="bg-blue-100 px-2 py-0.5 rounded text-xs font-mono">{'{{organisation_name}}'}</code>
                      <span className="text-xs">Your organisation</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="bg-blue-100 px-2 py-0.5 rounded text-xs font-mono">{'{{jobs_table}}'}</code>
                      <span className="text-xs">Schedule table</span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <EmailPreview
                emailScript={tempEmailScript}
                organisationName={settings?.organisation_name || 'Your Organisation'}
                sampleData={{
                  customer_name: form?.name || 'Sample Customer'
                }}
              />
            )}
          </div>
          <DialogFooter className="flex-shrink-0 gap-2 border-t pt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowEmailPreview(!showEmailPreview)}
              data-testid="toggle-customer-preview-btn"
            >
              <Eye className="h-4 w-4 mr-2" />
              {showEmailPreview ? 'Edit Script' : 'Preview Email'}
            </Button>
            <div className="flex-1" />
            <Button variant="outline" onClick={() => {
              setEmailScriptDialogOpen(false);
              setShowEmailPreview(false);
            }}>
              Cancel
            </Button>
            <Button 
              onClick={() => {
                setForm(prev => ({ ...prev, custom_schedule_email_script: tempEmailScript }));
                setEmailScriptDialogOpen(false);
                setShowEmailPreview(false);
                toast.success('Email script updated - remember to save');
              }}
              data-testid="save-customer-email-script-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Script
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Group Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Convert to Group</DialogTitle>
            <DialogDescription>
              Convert this independent customer to a group. This will allow you to add multiple sites to this customer.
              The existing site will remain as the first site under the group.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>Note:</strong> After conversion, you'll be able to add additional sites to "{customer?.name}".
                All existing job history and contacts will be preserved.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleConvertToGroup} disabled={converting}>
              {converting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Convert to Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Attach to Group Dialog */}
      <Dialog open={attachDialogOpen} onOpenChange={setAttachDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Attach to Group</DialogTitle>
            <DialogDescription>
              Move this independent customer's site to a group. The independent customer wrapper will be archived.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Select Group</Label>
            <Select value={selectedGroupId} onValueChange={setSelectedGroupId}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder="Choose a group" />
              </SelectTrigger>
              <SelectContent>
                {groupClients.map(g => (
                  <SelectItem key={g.id} value={g.id.toString()}>
                    <div className="flex items-center gap-2">
                      {g.colour && <div className="w-3 h-3 rounded-full" style={{ backgroundColor: g.colour }} />}
                      {g.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAttachDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAttachToGroup} disabled={!selectedGroupId || attaching}>
              {attaching && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Attach to Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
